/**
 * The one place this app talks to the backend.
 *
 * `/api/waitlist` is a **same-origin relative path**, deliberately. CloudFront serves the
 * static site and routes `/api/*` to the waitlist API under the same hostname, so the browser
 * never makes a cross-origin request — which means no CORS preflight, no `Access-Control-*`
 * headers to configure, and no allowed-origin list to get wrong. The most reliable way to
 * secure a cross-origin surface is not to have one.
 *
 * Never turn this into an absolute URL. Pointing at the `execute-api` hostname directly would
 * bypass CloudFront, and CloudFront is where WAF, rate limiting, TLS policy and the origin
 * secret all live.
 */

import { WAITLIST_COPY } from '../config/content';

const ENDPOINT = '/api/waitlist';

export type WaitlistResult = { ok: true } | { ok: false; message: string };

export async function submitWaitlist(email: string, source = 'landing-page'): Promise<WaitlistResult> {
  // A courtesy check so an obvious typo does not cost a round trip. The server re-validates;
  // this is not the control.
  const trimmed = email.trim();
  if (!trimmed.includes('@')) {
    return { ok: false, message: WAITLIST_COPY.invalidEmail };
  }

  let response: Response;
  try {
    response = await fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ email: trimmed, source }),
    });
  } catch {
    // Offline, DNS failure, request blocked. Nothing here is actionable for the visitor
    // beyond trying again, so do not speculate about the cause.
    return { ok: false, message: WAITLIST_COPY.networkError };
  }

  if (response.ok) {return {
     ok: true };
  }

  // The API returns `{ error: "<already user-safe text>" }`. It is written to be shown, but
  // this still guards the shape: if the body is missing, malformed, or an HTML error page from
  // some intermediary, fall back rather than rendering whatever arrived.
  try {
    const body: unknown = await response.json();
    const message =
      typeof body === 'object' && body !== null && 'error' in body && typeof body.error === 'string'
        ? body.error
        : WAITLIST_COPY.genericFailure;
    return { ok: false, message };
  } catch {
    return { ok: false, message: WAITLIST_COPY.genericFailure };
  }
}
